import React, { useCallback, useEffect, useState } from "react";
import { LINE_ID_COLOR } from "../../../../common/constant/colors";
import {
  EMAIL_VALIDATION_TEXT,
  MEMBER_REGISTRATION_PASSWORD_SUBTEXT,
  PASSWORD_VALIDATION_TEXT,
} from "../../../../common/constant/validations";
import { HeaderContainer } from "../../../../common/style";
import { useLineLink } from "../../../../lib/hooks/useLineLink";
import { LoginType, settingStore } from "../../../template/store/setting";
import { MemberContent } from "../../../template/style";
import { Button, TextField } from "../../../ui";
import { Selection } from "../../../ui/selection/Selection";
import {
  GENDER_SELECTION_OPTIONS,
  GENDER_VALIDATION_TEXT,
  MEMBER_REGISTRATION_STEP_IMAGES_PATH,
  MONTH_SELECTION_OPTIONS,
  MONTH_VALIDATION_TEXT,
  YEAR_SELECTION_OPTIONS,
  YEAR_VALIDATION_TEXT,
} from "../constant";
import useMemberRegistrationStore, {
  setMemberRegistrationConfirmPassword,
  setMemberRegistrationEmail,
  setMemberRegistrationGender,
  setMemberRegistrationMonth,
  setMemberRegistrationPassword,
  setMemberRegistrationYear,
} from "../redux/store";
import {
  ButtonContainer,
  Container,
  GeneratePasswordButton,
  StepImage,
  SubTitle,
} from "../style";

export const MemberRegistrationStepOneContainer: React.FC = () => {
  const email = useMemberRegistrationStore((state) => state.email);
  const gender = useMemberRegistrationStore((state) => state.gender);
  const year = useMemberRegistrationStore((state) => state.year);
  const month = useMemberRegistrationStore((state) => state.month);
  const password = useMemberRegistrationStore((state) => state.password);
  const confirmPassword = useMemberRegistrationStore(
    (state) => state.confirmPassword,
  );
  const emailError = useMemberRegistrationStore((state) => state.emailError);
  const genderError = useMemberRegistrationStore((state) => state.genderError);
  const yearError = useMemberRegistrationStore((state) => state.yearError);
  const monthError = useMemberRegistrationStore((state) => state.monthError);
  const passwordError = useMemberRegistrationStore(
    (state) => state.passwordError,
  );
  const confirmPasswordError = useMemberRegistrationStore(
    (state) => state.confirmPasswordError,
  );
  const [isGeneratePassword, setIsGeneratePassword] = useState<boolean>(false);
  const login_type: LoginType = settingStore((state) => state.login_type);

  const { showLineLink } = useLineLink("register");

  const generatePassword = useCallback(() => {
    const lowerCase = "abcdefghijklmnopqrstuvwxyz";
    const upperCase = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
    const numbers = "0123456789";
    const symbols = "!#%*,-./;<=>?@^_|";

    // パスワードに使用する文字列を設定
    let allCharacters: string = lowerCase + upperCase + numbers;
    if (process.env.REACT_APP_PASSWORD_SETTING === "hard") {
      allCharacters += symbols;
    }

    // パスワードの長さを取得
    const length = Number(process.env.REACT_APP_PASSWORD_DIGITS);

    // それぞれの文字セットから1文字ずつ必ず含める
    let password = "";
    password += lowerCase[Math.floor(Math.random() * lowerCase.length)];
    password += upperCase[Math.floor(Math.random() * upperCase.length)];
    password += numbers[Math.floor(Math.random() * numbers.length)];

    if (process.env.REACT_APP_PASSWORD_SETTING === "hard") {
      password += symbols[Math.floor(Math.random() * symbols.length)];
    }

    // 残りの文字をランダムに追加
    for (let i = password.length; i < length; i++) {
      const randomIndex = Math.floor(Math.random() * allCharacters.length);
      password += allCharacters[randomIndex];
    }

    // パスワードの文字をシャッフルしてランダム化
    password = password
      .split("")
      .sort(() => 0.5 - Math.random())
      .join("");

    setMemberRegistrationPassword(password);
    setMemberRegistrationConfirmPassword(password);
    setIsGeneratePassword(true);
  }, []);

  useEffect(() => {
    setMemberRegistrationPassword("");
    setMemberRegistrationConfirmPassword("");
  }, []);

  return (
    <MemberContent>
      <HeaderContainer>
        <h3>新規会員登録</h3>
      </HeaderContainer>

      <StepImage
        src={MEMBER_REGISTRATION_STEP_IMAGES_PATH.replace("{step}", "1")}
      />

      {(login_type === "line" || login_type === "email_line") && (
        <ButtonContainer>
          <Button
            text="LINE IDで登録"
            color={LINE_ID_COLOR}
            styling="border"
            fontWeight="bold"
            onClick={showLineLink}
          />
          <SubTitle>会員情報を入力してください</SubTitle>
        </ButtonContainer>
      )}

      <Container>
        <TextField
          type="email"
          name="email"
          label="メールアドレス"
          required
          placeholder={EMAIL_VALIDATION_TEXT}
          onInput={setMemberRegistrationEmail}
          error={emailError}
          value={email}
          disactived={true}
          filled={true}
        />

        <Selection
          name="gender"
          label="性別"
          required
          placeholder={GENDER_VALIDATION_TEXT}
          options={GENDER_SELECTION_OPTIONS}
          error={genderError}
          onSelection={setMemberRegistrationGender}
          value={gender ? gender.toString() : undefined}
        />

        <Selection
          name="year"
          label="生まれた年"
          required
          placeholder={YEAR_VALIDATION_TEXT}
          options={YEAR_SELECTION_OPTIONS}
          error={yearError}
          onSelection={setMemberRegistrationYear}
          value={year ? year.toString() : undefined}
          defaultValue={"1990"}
        />

        <Selection
          name="month"
          label="誕生月"
          required
          placeholder={MONTH_VALIDATION_TEXT}
          options={MONTH_SELECTION_OPTIONS}
          error={monthError}
          onSelection={setMemberRegistrationMonth}
          value={month ? month.toString() : undefined}
        />

        <TextField
          type="password"
          name="password"
          label="パスワード"
          required
          placeholder={PASSWORD_VALIDATION_TEXT}
          subtext={MEMBER_REGISTRATION_PASSWORD_SUBTEXT}
          onInput={setMemberRegistrationPassword}
          isGeneratePassword={isGeneratePassword}
          error={passwordError}
          value={password ? password : ""}
        >
          <GeneratePasswordButton onClick={generatePassword}>
            自動で生成する
          </GeneratePasswordButton>
        </TextField>

        <TextField
          type="password"
          name="confirmPassword"
          label="パスワード再入力"
          required
          placeholder={PASSWORD_VALIDATION_TEXT}
          onInput={setMemberRegistrationConfirmPassword}
          isGeneratePassword={isGeneratePassword}
          error={confirmPasswordError}
          value={confirmPassword ? confirmPassword : ""}
        />
      </Container>
    </MemberContent>
  );
};
